import { cookieService } from "@/lib/cookie/cookieService";
import { AppError } from "@/lib/utils/errors";
import { resetAllBusinessStores } from "@/lib/utils/state";
import { BusinessService } from "@/modules/services/business/businessService";
import { useBusinessInfoStore } from "@/modules/store/businessInfoStore";
import { BusinessShortInfo } from "@/modules/types/business";
import { useEffect, useState } from "react";

const getCookieDomain = () => {
  if (typeof window === "undefined") return undefined;
  
  const hostname = window.location.hostname;
  
  if (hostname.endsWith(".dev.togetherwork.vn")) {
    return ".dev.togetherwork.vn";
  }
  
  return undefined;
};

export const useBusinessInfo = () => {
  const {
    businessInfo,
    isLoadingBusiness,
    getBusinessInfo,
    setBusinessInfo,
    setBusinessPosts,
  } = useBusinessInfoStore();

  return {
    businessInfo,
    isLoadingBusiness,
    getBusinessInfo,
    setBusinessInfo,
    setBusinessPosts,
  };
};

export const useManagedBusinesses = () => {
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isFirstSelected, setIsFirstSelected] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessShortInfo>();
  const [managedBusinesses, setManagedBusinesses] = useState<
    BusinessShortInfo[]
  >([]);
  const { businessInfo, getBusinessInfo } = useBusinessInfo();

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      fetchManagedBusinesses();
      if (isFirstSelected) {
        setIsFirstSelected(false);
        const businessId = cookieService.get("businessId");
        if (businessId && businessInfo && businessInfo.id !== businessId) {
          getBusinessInfo({ businessId });
        }
      }
    }
  }, []);

  const fetchManagedBusinesses = async () => {
    const businessService = new BusinessService();
    try {
      const response = await businessService.getManagedBusinesses();
      if (response.businessInfo && response.businessInfo.length > 0) {
        setManagedBusinesses(response.businessInfo);
      }
    } catch (error) {
      const appErr = new AppError(error);
      appErr.logError(
        "src/modules/hooks/business/business.ts[fetchManagedBusinesses]"
      );
    }
  };

  useEffect(() => {
    if (managedBusinesses && businessInfo && !isFirstSelected) {
      setSelectedBusiness(
        managedBusinesses.find((business) => business.id === businessInfo.id)
      );
    }
  }, [businessInfo, managedBusinesses]);

  const onChangeBusiness = async (business: BusinessShortInfo) => {
    if (business.id === businessInfo?.id) return;

    resetAllBusinessStores();
    const { setIsSwitchingBusiness } = useBusinessInfoStore.getState();
    setIsSwitchingBusiness(true);
    await getBusinessInfo({ businessId: business.id });
    setSelectedBusiness(business);
    cookieService.set("businessId", business.id, { 
      path: "/", 
      expires: 30,
      domain: getCookieDomain(),
    });
    setIsSwitchingBusiness(false);
  };

  return {
    managedBusinesses,
    selectedBusiness,
    onChangeBusiness,
  };
};
